import React, { useContext, useEffect, useState } from 'react';
import { EmissionContext } from '../../../providers/EmissionProvider';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { Modal, ModalContent, ModalOverlay, Progress } from '@chakra-ui/react';
import { formatCellphone } from '../../../helpers/string';
import { ReactComponent as CellphoneIcon } from '../../../assets/images/icons/mevo/ic-cell-phone.svg';
import { ReactComponent as CheckIcon } from '../../../assets/images/icons/mevo/ic_check.svg';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { DialogWrappedComponentProps, FeedbackContext } from '../../../providers/FeedbackProvider';
import ClosePrescriptionBrowserTab from './ClosePrescriptionBrowserTab';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { APP_VIEWPORT_HEIGHT } from '../../../helpers/embeddedViewport';

export interface PrescriptionEmittionLoadingProps {
  emitted: boolean;
  isLoading: boolean;
  enableDialogCloseWindowTab: boolean;
}

const MAX_NON_EMITTED_BAR_VALUE = 90;
const EMITTED_BAR_VALUE = 100;
const PROGRESS_BAR_TOTAL_STEPS = 12;

export default function PrescriptionEmissionLoading({
  emitted,
  isLoading,
  enableDialogCloseWindowTab,
}: PrescriptionEmittionLoadingProps) {
  const { emissionPhone, emissionEmail } = useContext(EmissionContext);
  const [timerProgress, setTimerProgress] = useState(0);
  const [isOpen, setIsOpen] = useState(true);
  const { openBaseDialog } = useContext(FeedbackContext);
  const { prescriberConfiguration } = usePrescriptionStore();

  useEffect(() => {
    if (!emitted && !isLoading) return;

    let step = 0;

    const timer = setInterval(() => {
      setTimerProgress((prevProgress) => {
        if (emitted) {
          if (enableDialogCloseWindowTab) {
            setIsOpen(false);

            openBaseDialog((dialogProps: DialogWrappedComponentProps) => <ClosePrescriptionBrowserTab />, {
              width: 'auto',
              height: 'auto',
              persist: true,
              backgroundColor: 'transparent',
            });
          }
          clearInterval(timer);
          return EMITTED_BAR_VALUE;
        }

        if (step < PROGRESS_BAR_TOTAL_STEPS && prevProgress < MAX_NON_EMITTED_BAR_VALUE) {
          step++;
          return (step / PROGRESS_BAR_TOTAL_STEPS) * MAX_NON_EMITTED_BAR_VALUE;
        } else {
          clearInterval(timer);
          return prevProgress;
        }
      });
    }, 500);

    return () => clearInterval(timer);
  }, [emitted, enableDialogCloseWindowTab, isLoading, openBaseDialog]);

  if (!emitted && !isLoading) return null;

  return (
    <Modal isOpen={isOpen} onClose={() => {}} isCentered motionPreset="slideInBottom">
      <ModalOverlay />
      <ModalContent
        width={'100vw'}
        minW={'100vw'}
        height={APP_VIEWPORT_HEIGHT}
        p={0}
        bgColor={'rgba(255, 255, 255, 0.8)'}
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        borderRadius={'0px'}
      >
        <Flex
          justifyContent={'center'}
          alignItems={'center'}
          flexDir={'column'}
          h={'100%'}
          w={{ base: '100%', md: '500px' }}
          p={'15px'}
        >
          <Flex
            border={'4px solid'}
            borderColor={'#3D0E6B'}
            p={'10px'}
            borderRadius={'50%'}
            justifyContent={'center'}
            alignItems={'center'}
            w={'94px'}
            height={'94px'}
            position={'relative'}
            mb={'32px'}
          >
            {emitted && (
              <Box pos={'absolute'} top={'-10px'} right={'-10px'}>
                <CheckIcon width={44} />
              </Box>
            )}

            <CellphoneIcon />
          </Flex>

          <Text
            fontSize={'32px'}
            fontWeight={600}
            lineHeight={'32px'}
            mb={'5px'}
            color={'#3D0E6B'}
            textAlign={'center'}
          >
            Avise ao paciente para acessar os documentos em seu celular!
          </Text>

          {!prescriberConfiguration?.Features?.hidePatientContactInfo && (
            <Flex mb={'32px'} flexDirection={'column'} textAlign={'center'}>
              <Text fontSize={'18px'} fontWeight={300} color={'#16002C'} lineHeight={'19px'}>
                {emitted ? 'Enviado' : 'Enviando'} para o telefone <b>{emissionPhone}</b>
                {emissionEmail && ' e também para'}
              </Text>

              {emissionEmail && (
                <Text fontSize={'18px'} fontWeight={300} color={'#16002C'} lineHeight={'19px'}>
                  o e-mail <b>{emissionEmail}</b>
                </Text>
              )}
            </Flex>
          )}

          <Box w={'100%'}>
            <Progress
              colorScheme="green"
              size="sm"
              value={timerProgress}
              borderRadius={'8px'}
              sx={{
                '& > div:first-child': {
                  transitionProperty: 'width',
                },
              }}
            />
          </Box>
          <Text
            fontSize={'14px'}
            fontWeight={600}
            color={'#16002C'}
            data-testid={`emission-${emitted ? 'success' : 'loading'}`}
          >
            {!emitted ? 'Criando e assinando seus documentos...' : 'Tudo certo!'}
          </Text>
        </Flex>
      </ModalContent>
    </Modal>
  );
}
